import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Camera } from 'lucide-react';
import { useApp } from '../context/AppContext';
import useProfile from '../hooks/useProfile';

const ROLES = ['Admin', 'Manager', 'Viewer'];

function getInitials(firstName, lastName) {
  return (
    (firstName?.[0] || '').toUpperCase() + (lastName?.[0] || '').toUpperCase()
  );
}

export default function ProfileSlideout() {
  const { profileOpen, setProfileOpen } = useApp();
  const { profile, loading, updateProfile, updateAvatar } = useProfile();
  const location = useLocation();

  // ── Edit mode state ──
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [avatarError, setAvatarError] = useState('');

  // ── Auto-close on navigation (T011) ──
  useEffect(() => {
    if (profileOpen) {
      setProfileOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Reset edit state when panel closes ──
  useEffect(() => {
    if (!profileOpen) {
      setIsEditing(false);
      setErrors({});
      setAvatarError('');
    }
  }, [profileOpen]);

  const handleClose = useCallback(() => {
    setProfileOpen(false);
  }, [setProfileOpen]);

  // ── Edit handlers (T012-T015) ──
  const handleEdit = useCallback(() => {
    setFormData({
      firstName: profile.firstName,
      lastName: profile.lastName,
      description: profile.description,
      role: profile.role,
    });
    setErrors({});
    setIsEditing(true);
  }, [profile]);

  const handleCancel = useCallback(() => {
    setFormData({});
    setErrors({});
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await updateProfile({
      ...profile,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      description: formData.description?.trim() || '',
      role: formData.role,
    });
    setIsEditing(false);
    setErrors({});
  }, [formData, profile, updateProfile]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // ── Avatar upload (T016-T018) ──
  const handleAvatarChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError('');
    try {
      await updateAvatar(file);
    } catch (err) {
      setAvatarError(err.message);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }, [updateAvatar]);

  if (!profileOpen) return null;

  const initials = getInitials(profile.firstName, profile.lastName);

  return (
    <>
      {/* Backdrop (T008) */}
      <div className="profile-backdrop" onClick={handleClose} />

      {/* Panel */}
      <div
        className="profile-slideout"
        role="dialog"
        aria-label="User profile"
      >
        <button
          className="profile-close-btn"
          onClick={handleClose}
          aria-label="Close profile"
        >
          <X size={18} />
        </button>

        {/* Avatar header */}
        <div className="profile-header">
          <label className="profile-avatar-wrapper" aria-label="Upload profile picture">
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
            {profile.avatarUrl ? (
              <img
                className="profile-avatar-img"
                src={profile.avatarUrl}
                alt={`${profile.firstName} ${profile.lastName}`}
              />
            ) : (
              <span className="profile-avatar-initials">{initials}</span>
            )}
            <span className="profile-avatar-overlay">
              <Camera size={20} />
            </span>
          </label>

          {avatarError && (
            <span className="profile-field-error" role="alert">
              {avatarError}
            </span>
          )}

          <span className="profile-display-name">
            {profile.firstName} {profile.lastName}
          </span>
          <span className="profile-role-badge">{profile.role}</span>
        </div>

        <div className="profile-divider" />

        {/* Fields */}
        {isEditing ? (
          /* ── Edit mode ── */
          <>
            <div className="profile-field">
              <div className="profile-field-label">Email</div>
              <div className="profile-field-value">{profile.email}</div>
            </div>

            <div className="profile-field">
              <label className="profile-field-label" htmlFor="profile-firstName">
                First Name *
              </label>
              <input
                id="profile-firstName"
                className={`profile-input${errors.firstName ? ' has-error' : ''}`}
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
              />
              {errors.firstName && (
                <div className="profile-field-error" role="alert">
                  {errors.firstName}
                </div>
              )}
            </div>

            <div className="profile-field">
              <label className="profile-field-label" htmlFor="profile-lastName">
                Last Name *
              </label>
              <input
                id="profile-lastName"
                className={`profile-input${errors.lastName ? ' has-error' : ''}`}
                type="text"
                value={formData.lastName || ''}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
              />
              {errors.lastName && (
                <div className="profile-field-error" role="alert">
                  {errors.lastName}
                </div>
              )}
            </div>

            <div className="profile-field">
              <label className="profile-field-label" htmlFor="profile-description">
                Description
              </label>
              <textarea
                id="profile-description"
                className="profile-input profile-textarea"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
              />
            </div>

            <div className="profile-field">
              <label className="profile-field-label" htmlFor="profile-role">
                Role
              </label>
              <select
                id="profile-role"
                className="profile-select"
                value={formData.role || 'Admin'}
                onChange={(e) => handleFieldChange('role', e.target.value)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="profile-actions">
              <button className="btn" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          /* ── View mode ── */
          <>
            <div className="profile-field">
              <div className="profile-field-label">Email</div>
              <div className="profile-field-value">{profile.email}</div>
            </div>

            <div className="profile-field">
              <div className="profile-field-label">First Name</div>
              <div className="profile-field-value">{profile.firstName}</div>
            </div>

            <div className="profile-field">
              <div className="profile-field-label">Last Name</div>
              <div className="profile-field-value">{profile.lastName}</div>
            </div>

            <div className="profile-field">
              <div className="profile-field-label">Description</div>
              <div className={`profile-field-value${!profile.description ? ' empty' : ''}`}>
                {profile.description || '—'}
              </div>
            </div>

            <div className="profile-field">
              <div className="profile-field-label">Role</div>
              <div className="profile-field-value">{profile.role}</div>
            </div>

            <div className="profile-actions">
              <button className="btn btn-primary" onClick={handleEdit}>
                Edit
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
